/**
 * Sends the buyer to checkout with the bespoke deposit and nothing else.
 *
 * Shopify gives a shop one cart, so "check out just this item" means emptying the cart first. Doing
 * that silently would throw away whatever the buyer had collected, so the cart is stashed before it
 * is cleared and put back the next time they land on a page that loads this script.
 *
 * Without JavaScript the form still submits normally: the deposit is added and the whole cart goes
 * to checkout. Worse, but not broken.
 */

const STASH_KEY = 'yanyi:bespoke:stashed-cart';

/** @param {string} url @param {unknown} [body] */
async function cartRequest(url, body) {
  const response = await fetch(url, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url} responded ${response.status}`);
  return response.json();
}

/**
 * Puts back a cart stashed by an earlier bespoke checkout.
 *
 * Runs on every page that loads this script. It is deliberately forgiving: a line that can no longer
 * be added (sold out, deleted) is skipped rather than aborting the rest, and the stash is dropped
 * either way so a bad entry cannot wedge the buyer's cart forever.
 */
async function restoreStashedCart() {
  let stashed;
  try {
    stashed = JSON.parse(localStorage.getItem(STASH_KEY) ?? 'null');
  } catch {
    localStorage.removeItem(STASH_KEY);
    return;
  }
  if (!Array.isArray(stashed) || stashed.length === 0) {
    localStorage.removeItem(STASH_KEY);
    return;
  }

  // Clear first: if this throws, the stash survives and we try again next page load.
  localStorage.removeItem(STASH_KEY);

  for (const line of stashed) {
    try {
      await cartRequest('/cart/add.js', {
        id: line.id,
        quantity: line.quantity,
        properties: line.properties ?? {},
      });
    } catch (error) {
      console.warn('[bespoke] could not restore a cart line:', error);
    }
  }

  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
}

class BespokeCheckout extends HTMLElement {
  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#form?.addEventListener('submit', this.#handleSubmit);
    restoreStashedCart();
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#handleSubmit);
  }

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const variantId = this.dataset.variantId;
    if (!variantId) return; // Let the plain form submission handle it.

    event.preventDefault();

    const button = this.#form?.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    /** @type {Record<string, string>} */
    let properties = {};
    try {
      properties = JSON.parse(this.dataset.properties || '{}');
    } catch {
      properties = {};
    }

    try {
      const cart = await cartRequest('/cart.js');

      if (cart.item_count > 0) {
        localStorage.setItem(
          STASH_KEY,
          JSON.stringify(
            cart.items.map((/** @type {any} */ item) => ({
              id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties ?? {},
            }))
          )
        );
      }

      await cartRequest('/cart/clear.js', {});
      await cartRequest('/cart/add.js', { id: Number(variantId), quantity: 1, properties });

      window.location.href = '/checkout';
    } catch (error) {
      console.warn('[bespoke] falling back to a normal submit:', error);
      // Whatever went wrong, the buyer should still be able to buy: let the form through.
      if (button instanceof HTMLButtonElement) button.disabled = false;
      this.#form?.submit();
    }
  };
}

if (!customElements.get('bespoke-checkout')) {
  customElements.define('bespoke-checkout', BespokeCheckout);
}
